import { forwardRef, useContext, useState } from 'react'
import { useSwipeable } from 'react-swipeable'
import { navigate } from 'gatsby-plugin-intl'

import JumpLink from '@molecules/JumpLink'
import ArrowLeft from '@icons/ArrowLeft'
import ArrowRitght from '@icons/ArrowRight'
import MaskedIcon from '@icons/Masked'
import { useTranslation } from '@models/Translation'
import { useArtisanArticles } from '@models/Artisan'
import { StoreContext } from '@models/Store'

import clementineSandnerImage from './assets/clementine-sandner.jpg'
import allanWestImage from './assets/allan-west.jpg'
import emilieEvenImage from './assets/emilie-even.jpg'
import johnOgleveeImage from './assets/john-oglevee.jpg'
import mathildeRoseanneBregeonImage from './assets/mathilde-roseanne-bregeon.jpg'
import veraBrackloImage from './assets/vera-bracklo.webp'
import tulikaRanjanImage from './assets/tulika-ranjan.jpg'
import ericChevallierImage from './assets/eric-chevallier.jpg'
import steveBeimelImage from './assets/steve-beimel.jpg'

import * as style from './style.module.sass'

const IMAGE_MAP: { [id: string]: string } = {
  clementine_sandner: clementineSandnerImage,
  allan_west: allanWestImage,
  emilie_even: emilieEvenImage,
  john_oglevee: johnOgleveeImage,
  mathilde_roseanne_bregeon: mathildeRoseanneBregeonImage,
  vera_bracklo: veraBrackloImage,
  tulika_ranjan: tulikaRanjanImage,
  eric_chevallier: ericChevallierImage,
  steve_beimel: steveBeimelImage,
}

const ArtisanColumn = forwardRef<HTMLElement>((_, sectionRef) => {
  const { entity } = useContext(StoreContext)
  const { t, locale } = useTranslation()
  const articles = useArtisanArticles()
  const columnArticles = articles
    .filter(article => article.data.category === 'series')
    .slice(0, 3)
    .reverse()

  const [selectedIndex, setSelectedIndex] = useState(1)
  const onSwipedLeft = () => setSelectedIndex(Math.min(2, selectedIndex + 1))
  const onSwipedRight = () => setSelectedIndex(Math.max(0, selectedIndex - 1))
  const handlers = useSwipeable({ onSwipedLeft, onSwipedRight })
  const titleHandlers = useSwipeable({ onSwipedLeft, onSwipedRight })
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 })
  const [cursorType, setCursorType] = useState<string>('disabled')

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    setMousePosition({
      x: e.clientX - 70,
      y: e.clientY + window.scrollY - e.currentTarget.offsetTop - 70,
    })
  }

  const clickCard = (index: number) => {
    if (index === selectedIndex) {
      navigate(columnArticles[index].path)
      return
    }
    setSelectedIndex(index)
  }

  return (
    <section className={style.main} ref={sectionRef}>
      <div
        className={style.copy}
        dangerouslySetInnerHTML={{ __html: t('artisan.columnCopy') }}
        data-locale={locale}
      />

      <div {...handlers} onMouseMove={handleMouseMove}>
        <div className={style.images}>
          {columnArticles.map((article, index) => {
            const position = index - selectedIndex + 1
            const type =
              position === 0 ? 'prev' : position === 1 ? 'detail' : 'next'

            return (
              <button
                className={style.image}
                onClick={() => clickCard(index)}
                data-position={position}
                onMouseEnter={() => setCursorType(type)}
                onMouseLeave={() => setCursorType('disabled')}
                key={`column-${index}`}
              >
                <img src={IMAGE_MAP[article.blogId]} />
              </button>
            )
          })}
          <div
            className={style.cursor}
            style={{
              top: `${mousePosition.y}px`,
              left: `${mousePosition.x}px`,
            }}
            data-type={cursorType}
          >
            <svg width="23" height="23" viewBox="0 0 23 23" fill="none">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M11.968 20.64C16.8281 20.64 20.768 16.7001 20.768 11.84C20.768 6.97993 16.8281 3.04004 11.968 3.04004C7.10786 3.04004 3.16797 6.97993 3.16797 11.84C3.16797 16.7001 7.10786 20.64 11.968 20.64ZM11.4781 8.72478C11.1406 8.49976 10.7066 8.47878 10.3489 8.6702C9.99125 8.86162 9.76797 9.23436 9.76797 9.64004V14.04C9.76797 14.4457 9.99125 14.8185 10.3489 15.0099C10.7066 15.2013 11.1406 15.1803 11.4781 14.9553L14.7781 12.7553C15.0842 12.5513 15.268 12.2078 15.268 11.84C15.268 11.4723 15.0842 11.1288 14.7781 10.9248L11.4781 8.72478Z"
                fill="white"
              />
            </svg>
          </div>

          <div className={style.headline}>
            <h2>Special Column</h2>
            <div
              className={style.sub}
              dangerouslySetInnerHTML={{
                __html: t('artisan.columnHeadline'),
              }}
            />
          </div>
        </div>
      </div>

      <div className={style.controller}>
        {Array.from({ length: 3 }).map((_, index) => (
          <button
            key={`index-${index}`}
            onClick={() => setSelectedIndex(index)}
            data-active={index === selectedIndex}
          />
        ))}
      </div>

      <div className={style.description} {...titleHandlers}>
        <button onClick={onSwipedRight}>
          <ArrowLeft />
        </button>
        <div className={style.articleTitle}>
          {columnArticles[selectedIndex].title}
          {entity?.user?.isSignIn !== true &&
            columnArticles[selectedIndex].isMembersOnly && <MaskedIcon />}
        </div>
        <button onClick={onSwipedLeft}>
          <ArrowRitght />
        </button>
      </div>

      <div className={style.link}>
        <JumpLink to="/series/artisan_special_column">
          {t('artisan.columnLink')}
        </JumpLink>
      </div>
    </section>
  )
})

export default ArtisanColumn
